// Порядок и перетаскивание колонок — общая механика для всех гридов.
//
// Была только в гриде задач: заголовочная ячейка с нативным HTML5 drag-and-drop
// плюс сборка колонок по ключам из профиля. Здесь то же самое, но параметризовано
// профилем, поэтому подключается к любому списку одной строкой.
import { useState, type HTMLAttributes, type ReactElement } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table/interface';

import type { GridProfile } from './useGridProfile.js';

interface DraggableHeaderCellProps extends HTMLAttributes<HTMLTableCellElement> {
  colIndex?: number;
  onColDragStart?: (index: number) => void;
  onColDragOver?: (index: number) => void;
  onColDrop?: (index: number) => void;
  onColDragEnd?: () => void;
  insertSide?: 'left' | 'right' | null;
  dragging?: boolean;
}

/** Заголовок с индикатором места вставки: видно, куда встанет колонка. */
export function DraggableHeaderCell({
  colIndex,
  onColDragStart,
  onColDragOver,
  onColDrop,
  onColDragEnd,
  insertSide,
  dragging,
  style,
  ...rest
}: DraggableHeaderCellProps): ReactElement {
  const draggable = colIndex !== undefined;
  const indicator =
    insertSide === 'left'
      ? { boxShadow: 'inset 3px 0 0 0 var(--ant-color-primary-border-hover, #d97706)' }
      : insertSide === 'right'
        ? { boxShadow: 'inset -3px 0 0 0 var(--ant-color-primary-border-hover, #d97706)' }
        : null;

  return (
    <th
      {...rest}
      draggable={draggable}
      onDragStart={() => {
        if (colIndex !== undefined) onColDragStart?.(colIndex);
      }}
      onDragOver={(event) => {
        if (colIndex === undefined) return;
        event.preventDefault();
        onColDragOver?.(colIndex);
      }}
      onDrop={(event) => {
        if (colIndex === undefined) return;
        event.preventDefault();
        onColDrop?.(colIndex);
      }}
      onDragEnd={() => onColDragEnd?.()}
      style={{
        ...style,
        ...(indicator ?? {}),
        ...(dragging ? { opacity: 0.5 } : {}),
        cursor: draggable ? 'grab' : undefined,
      }}
    />
  );
}

export interface OrderedColumnsResult<T> {
  columns: ColumnsType<T>;
  /** Прокидывается в `Table`/`EntityTable` — включает перетаскивание заголовков. */
  components: { header: { cell: typeof DraggableHeaderCell } };
}

export interface UseOrderedColumnsOptions<T> {
  profile: GridProfile;
  /** Колонки по ключу — тот же ключ, что и в профиле. */
  columnMap: Record<string, ColumnType<T>>;
  /** Колонки, не имеющие смысла в контексте (например «Сделка» на карточке сделки). */
  suppress?: readonly string[];
  /** Колонки в конце, вне управления видом: «Действия». */
  trailing?: ColumnsType<T>;
}

export function useOrderedColumns<T>({
  profile,
  columnMap,
  suppress = [],
  trailing = [],
}: UseOrderedColumnsOptions<T>): OrderedColumnsResult<T> {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const visibleKeys = profile.visibleOrder.filter(
    (key) => !suppress.includes(key) && columnMap[key] !== undefined,
  );

  const columns: ColumnsType<T> = visibleKeys.map((key, index) => ({
    ...(columnMap[key] as ColumnType<T>),
    onHeaderCell: () =>
      ({
        colIndex: index,
        onColDragStart: (i: number) => setDragIndex(i),
        onColDragOver: (i: number) => setOverIndex(i),
        onColDrop: (i: number) => {
          const from = visibleKeys[dragIndex ?? -1];
          const to = visibleKeys[i];
          if (from && to && from !== to) profile.moveColumn(from, to);
          setDragIndex(null);
          setOverIndex(null);
        },
        onColDragEnd: () => {
          setDragIndex(null);
          setOverIndex(null);
        },
        insertSide:
          dragIndex !== null && overIndex === index && dragIndex !== index
            ? dragIndex < index
              ? 'right'
              : 'left'
            : null,
        dragging: dragIndex === index,
      }) as unknown as HTMLAttributes<HTMLElement>,
  }));

  return {
    columns: [...columns, ...trailing],
    components: { header: { cell: DraggableHeaderCell } },
  };
}
